import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  audio?: {
    title: string;
    artist: string;
    duration: number;
  };
  naatKhawan?: {
    name: string;
    naatCount: number;
  };
}

const SEO: React.FC<SEOProps> = ({
  title = 'NaatCity - Islamic Audio Streaming Platform',
  description = 'Discover the largest collection of Islamic Naats, Hamds, and spiritual recitations. Stream high-quality Islamic audio content online for free.',
  keywords = 'naat, islamic naats, hamd, qawwali, naat khawan, islamic audio',
  image = 'https://naatcity.com/og-image.jpg',
  url = 'https://naatcity.com',
  type = 'website',
  audio,
  naatKhawan
}) => {
  const siteTitle = title.includes('NaatCity') ? title : `${title} | NaatCity`;

  // Create structured data based on content type
  const structuredData = audio ? {
    "@context": "https://schema.org",
    "@type": "AudioObject",
    "name": audio.title,
    "creator": {
      "@type": "Person",
      "name": audio.artist
    },
    "duration": `PT${Math.floor(audio.duration / 60)}M${audio.duration % 60}S`,
    "encodingFormat": "audio/mpeg",
    "url": url
  } : naatKhawan ? {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": naatKhawan.name,
    "jobTitle": "Naat Khawan",
    "url": url,
    "description": `${naatKhawan.name} is a renowned Naat Khawan with ${naatKhawan.naatCount} naats available on NaatCity.`
  } : {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "NaatCity",
    "url": "https://naatcity.com",
    "description": description,
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://naatcity.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{siteTitle}</title>
      <meta name="title" content={siteTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="NaatCity" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={siteTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />

      {/* Audio specific meta tags if applicable */}
      {audio && (
        <>
          <meta property="og:audio" content={url} />
          <meta property="og:audio:type" content="audio/mpeg" />
          <meta property="music:duration" content={audio.duration.toString()} />
          <meta property="music:musician" content={audio.artist} />
        </>
      )}

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;